import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useInfiniteQuery } from 'react-query'

import ChangeStateTabs from 'components/UI/ChangeStateTabs/ChangeStateTabs'
import { usePageTitle } from 'context/pageTitleContext'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import UsersList from 'features/UsersList/UsersList'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import './_friends.scss'
import { getFriendRequests, getFriends } from 'services/endpoints/friendship'
import { type FriendsSorting } from 'types/api/friendship'
import { type ListFilter } from 'types/types'

const defaultSorting: FriendsSorting = {
    sort: 'created_at',
    order: ''
}

const getNextPageParam = (lastPage: any) => {
    const { current_page, last_page } = lastPage.meta
    return current_page < last_page ? current_page + 1 : false
}

export const Friends = () => {
    const { setTitle } = usePageTitle()
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [activeTab, setActiveTab] = useState<string>('all')
    const [sorting, setSorting] = useState<FriendsSorting>(defaultSorting)
    const [rawFriends, setRawFriends] = useState<any[]>([])
    const [friends, setFriends] = useState<any[]>([])
    const [rawRequests, setRawRequests] = useState<any[]>([])
    const [friendsRequests, setFriendsRequests] = useState<any[]>([])
    const [friendsNumber, setFriendsNumber] = useState<string | number>('-')
    const [friendsRequestsNumber, setFriendsRequestsNumber] = useState<
        number | undefined
    >(undefined)

    const { t } = useTranslation()

    const queryParameters = {
        sort: sorting.sort,
        sort_value: sorting.order,
    }

    const {
        data: friendsData,
        hasNextPage: hasNextFriendsPage,
        fetchNextPage: fetchNextFriendsPage,
        refetch: refetchFriends
    } = useInfiniteQuery(
        ['friends'],
        async ({ pageParam = 1 }) =>
            await getFriends({
                page: pageParam,
                ...queryParameters
            }),
        {
            getNextPageParam
        }
    )

    const {
        data: friendsRequestsData,
        hasNextPage: hasNextFriendsRequestsPage,
        fetchNextPage: fetchNextFriendsRequestsPage,
        refetch: refetchFriendsRequests
    } = useInfiniteQuery(
        ['friendsRequests'],
        async ({ pageParam = 1 }) =>
            await getFriendRequests({
                page: pageParam,
                ...queryParameters
            }),
        {
            getNextPageParam
        }
    )

    useEffect(() => {
        setTitle('Friends')
    }, [setTitle])

    useEffect(() => {
        const flat = friendsData?.pages?.reduce((all, page) => all.concat(page.data), [])
        setRawFriends(flat)
        setFriendsNumber(friendsData?.pages[0]?.meta?.total ?? '-')
    
        // Filter locally
        if (searchTerm.trim()) {
            const filtered = flat?.filter((u: { name?: string; username?: string }) =>
                u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                u.username?.toLowerCase().includes(searchTerm.toLowerCase())
            )
            setFriends(filtered)
        } else {
            setFriends(flat)
        }
    }, [friendsData, searchTerm])

    useEffect(() => {
        const flat = friendsRequestsData?.pages?.reduce((all, page) => all.concat(page.data), [])
        setRawRequests(flat)
        setFriendsRequestsNumber(friendsRequestsData?.pages[0]?.meta?.total)
    
        // Filter locally
        if (searchTerm.trim()) {
            const filtered = flat?.filter((u: { name?: string; username?: string }) =>
                u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                u.username?.toLowerCase().includes(searchTerm.toLowerCase())
            )
            setFriendsRequests(filtered)
        } else {
            setFriendsRequests(flat)
        }
    }, [friendsRequestsData, searchTerm])

    const renderUsersList = () => {
        switch (activeTab) {
            case 'requests':
                return (
                    <UsersList
                        activeTab={activeTab}
                        users={friendsRequests || []}
                        userCardType='friendReq'
                        loadMoreCb={handleLoadMoreFriendsRequests}
                    />
                )

            default:
                return (
                    <UsersList
                        activeTab={activeTab}
                        users={friends || []}
                        userCardType='friend'
                        loadMoreCb={handleLoadMoreFriends}
                    />
                )
        }
    }

    const handleLoadMoreFriends = () => {
        if (hasNextFriendsPage) {
            fetchNextFriendsPage()
        }
    }

    const handleLoadMoreFriendsRequests = () => {
        if (hasNextFriendsRequestsPage) {
            fetchNextFriendsRequestsPage()
        }
    }

    const filterButtons: ListFilter[] = [
        {
            type: 'sort',
            elements: {
                first_section: [
                    {
                        value: 'created_at',
                        name: t('mostRecent'),
                        default: sorting.sort === 'created_at'
                    },
                    {
                        value: 'name',
                        name: t('name'),
                        default: sorting.sort === 'name'
                    }
                ],
                second_section: [
                    {
                        value: 'asc',
                        name: t('ascending'),
                        default: sorting.order === 'asc'
                    },
                    {
                        value: 'desc',
                        name: t('descending'),
                        default: sorting.order === 'desc'
                    }
                ]
            }
        }
    ]

    const applySorting = (
        val: 'name' | 'created_at',
        val1?: 'desc' | 'asc' | ''
    ) => {
        setSorting({
            sort: val,
            order: val1 ?? defaultSorting.order
        })
    }

    useEffect(() => {
        switch (activeTab) {
            case 'requests':
                refetchFriendsRequests()
                break
            default:
                refetchFriends()
        }
    }, [sorting])

    return (
        <>
            <WithHeaderSection
                customClass='friends-whs'
                headerSection={
                    <LayoutHeader
                        type='search-with-buttons'
                        searchValue={searchTerm}
                        searchFn={(term: string) => {
                            setSearchTerm(term)
                        }}
                        clearFn={() => {
                            setSearchTerm('')
                        }}
                        additionalProps={{ placeholder: t('searchFriends') }}
                        buttons={filterButtons}
                        applyFn={(
                            val: 'name' | 'created_at',
                            val1?: 'desc' | 'asc' | ''
                        ) => {
                            applySorting(val, val1)
                        }}
                    />
                }
            >
                <>
                    <div className='list-header'>
                        <ChangeStateTabs
                            activeTab={activeTab}
                            tabs={[
                                {
                                    value: 'all',
                                    name: t('All')
                                },
                                {
                                    value: 'requests',
                                    name: t('requests')
                                }
                            ]}
                            clickFn={(val: string) => {
                                setActiveTab(val)
                            }}
                            width='fit'
                            hasInfoCircle={!!friendsRequestsNumber}
                            infoCircleData={friendsRequestsNumber}
                        />
                        <div className='friends-number'>
                            <span>{friendsNumber}</span> {t('friends')}
                        </div>
                    </div>
                    {renderUsersList()}
                </>
            </WithHeaderSection>
            <DesktopAdditionalContent />
        </>
    )
}
